// ContinuationScreen — landing page for a "Paiper Phone" continuation link
// (paiperclip.com/c/TOKEN), texted mid-call by a phone agent so the customer
// can pick up on the web. Two shapes, decided by the server:
//
//   kind "session" — sign the browser in as the call's callerUid and hand
//   off into the existing FormChatScreen in "companion" mode (banner while
//   the call is live, voice disabled, camera pinned, latest relevant bubble
//   highlighted).
//
//   kind "photos" — a standalone picker so the customer can drop in photos
//   the agent asked for, without joining the live chat.
//
//   kind "rebook" / "offer" — redeem the link (binds the anonymous customer
//   to their earlier submission) and open the agent's chat; offered times are
//   shown in a banner.
//
// Expired/unknown tokens redirect to the plain form (if we can still
// identify it) or show a neutral "link expired" card.

import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import {
  Box,
  Typography,
  Button,
  Card,
  CircularProgress,
  IconButton,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useBusiness from '../hooks/useBusiness';
import { BusinessThemeProvider } from '../context/BusinessThemeContext';
import BusinessLogo from '../components/BusinessLogo';
import { tok, DISPLAY_FONT } from '../theme/businessTheme';
import ErrorScreen from '../components/ErrorScreen';
import {
  fetchContinuation,
  redeemRebook,
  attachPhonePhotos,
  ContinuationExpiredError,
  ContinuationPhotos,
} from '../services/PhoneService';
import { uploadFormImage } from '../services/ImageUploadService';
import { CompanionState } from '../screens/FormChatScreen';

const MAX_PHOTOS = 10;

interface StagedPhoto {
  id: string;
  file: File;
  previewUrl: string;
}

const ContinuationScreen: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'expired' | 'photos'>('loading');
  const [photosData, setPhotosData] = useState<ContinuationPhotos | null>(null);

  useEffect(() => {
    let cancelled = false;

    const resolveToken = async () => {
      if (!token) {
        if (!cancelled) setStatus('expired');
        return;
      }

      try {
        const data = await fetchContinuation(token);

        if (cancelled) return;

        if (data.kind === 'session') {
          try {
            await signInWithCustomToken(getAuth(), data.customToken);
          } catch (error) {
            console.error('ContinuationScreen: custom token sign-in failed:', error);
          }

          const companion: CompanionState = {
            token,
            businessName: data.businessName,
            callActive: data.callActive,
            reason: data.reason,
            reportId: data.reportId,
          };

          navigate(`/form/${data.formId}`, { replace: true, state: { companion, fromLink: true } });
          return;
        }

        if (data.kind !== 'photos') {
          // Bind this browser to the earlier submission, then open the agent.
          // Offered times (owner "suggest another time") ride along as router
          // state so FormChatScreen can show a banner.
          // A failed redeem (too many devices, expired) still opens the agent,
          // just without binding this browser to the earlier submission.
          // Sign in as the uid that owns the original thread so the earlier
          // conversation (call transcript, booking messages) is what opens.
          if (data.customToken) {
            try {
              await signInWithCustomToken(getAuth(), data.customToken);
            } catch (error) {
              console.error('ContinuationScreen: custom token sign-in failed:', error);
            }
          }
          const redeemed = await redeemRebook(token).catch(() => ({
            formId: data.formId, offered: data.offered ?? null,
          }));
          if (cancelled) return;
          const offered = redeemed.offered ?? data.offered ?? null;
          navigate(`/form/${redeemed.formId || data.formId}`, {
            replace: true,
            state: {
              fromLink: true,
              ...(offered && offered.length ? { offered, offerBusinessName: data.businessName } : {}),
            },
          });
          return;
        }

        setPhotosData(data);
        setStatus('photos');
      } catch (error) {
        if (cancelled) return;

        if (error instanceof ContinuationExpiredError && error.formId) {
          navigate(`/form/${error.formId}`, { replace: true });
          return;
        }

        console.error('ContinuationScreen: failed to resolve continuation link:', error);
        setStatus('expired');
      }
    };

    resolveToken();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress size={48} thickness={3.5} sx={{ mb: 3, color: 'primary.main' }} />
        <Typography variant="body1" color="text.secondary">
          Connecting you…
        </Typography>
      </Box>
    );
  }

  if (status === 'expired') {
    return (
      <ErrorScreen
        title="Link expired"
        message="This link has expired. Call again to get a new one."
        buttonText="Go Home"
        onButtonClick={() => navigate('/')}
      />
    );
  }

  // status === 'photos'
  return <PhoneAttachScreen data={photosData!} token={token!} />;
};

// -----------------------------------------------------------------------
// Attach-mode: drop in photos the phone agent asked for, no chat involved.
// -----------------------------------------------------------------------

// Branded wrapper: the business's logo + theme (same tokens the customer apps
// use) frame the picker. Neutral spinner until the brand arrives so the page
// never flashes the default green before switching.
const PhoneAttachScreen: React.FC<{ data: ContinuationPhotos; token: string }> = ({ data, token }) => {
  const { business, loading } = useBusiness(data.channelId);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: 'background.default' }}>
        <CircularProgress size={40} thickness={3.5} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }
  return (
    <BusinessThemeProvider business={business}>
      <PhoneAttachContent data={data} token={token} />
    </BusinessThemeProvider>
  );
};

const PhoneAttachContent: React.FC<{ data: ContinuationPhotos; token: string }> = ({ data, token }) => {
  const { business } = useBusiness(data.channelId);
  const name = business.displayName || data.businessName;
  const questions = (data.questions || []).filter((q) => typeof q === 'string' && q.trim());
  const navigate = useNavigate();
  const [photos, setPhotos] = useState<StagedPhoto[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progressText, setProgressText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [expired, setExpired] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const idRef = useRef(0);

  useEffect(() => {
    return () => {
      photos.forEach((p) => URL.revokeObjectURL(p.previewUrl));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;

    setError(null);
    setPhotos((prev) => {
      const room = Math.max(0, MAX_PHOTOS - prev.length);
      const toAdd = files.slice(0, room).map((file) => ({
        id: `${Date.now()}_${idRef.current++}`,
        file,
        previewUrl: URL.createObjectURL(file),
      }));
      if (files.length > room) {
        setError(`Only ${MAX_PHOTOS} photos can be attached at once.`);
      }
      return [...prev, ...toAdd];
    });
  };

  const removePhoto = (id: string) => {
    setPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const handleDone = async () => {
    if (photos.length === 0 || uploading) return;
    setUploading(true);
    setError(null);

    try {
      const imageUrls: string[] = [];
      for (let i = 0; i < photos.length; i++) {
        setProgressText(`Uploading photo ${i + 1} of ${photos.length}…`);
        const url = await uploadFormImage({
          file: photos[i].file,
          channelUid: data.channelId,
          formId: data.formId,
        });
        imageUrls.push(url);
      }

      setProgressText(`Sending to ${name}…`);
      await attachPhonePhotos(token, imageUrls);

      setDone(true);
    } catch (err) {
      if (err instanceof ContinuationExpiredError) {
        setExpired(true);
      } else {
        console.error('PhoneAttachScreen: failed to send photos:', err);
        setError(
          err instanceof Error ? err.message : 'Sorry, those photos could not be sent. Please try again.'
        );
      }
    } finally {
      setUploading(false);
      setProgressText(null);
    }
  };

  if (expired) {
    return (
      <ErrorScreen
        title="Link expired"
        message={`This link has expired. Call ${name} again to get a new one.`}
        buttonText="Go Home"
        onButtonClick={() => navigate('/')}
      />
    );
  }

  // The business's own hero: logo + name, then the headline. When the agent
  // asked for a specific photo, that question IS the headline — no separate
  // callout — so the page reads as the business's own screen for this task.
  const headline = questions.length === 1 ? questions[0] : 'Add photos to your request';
  const header = (headlineText?: string, extra?: React.ReactNode) => (
    <Box
      component="header"
      sx={{
        flexShrink: 0,
        background: tok('background'),
        color: tok('onBackground'),
        px: 2.5,
        pt: 'max(16px, env(safe-area-inset-top))',
        pb: headlineText ? 4 : 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <BusinessLogo name={name} logoUrl={business.logoUrl} size={44} radius="50%" />
        <Box sx={{ minWidth: 0 }}>
          <Typography component="div" noWrap sx={{ fontSize: 17, fontWeight: 700, lineHeight: 1.25, fontFamily: DISPLAY_FONT }}>
            {name}
          </Typography>
          {business.copy.eyebrow && (
            <Typography component="div" noWrap sx={{ fontSize: 12, letterSpacing: '0.08em', color: tok('onBackgroundMuted') }}>
              {business.copy.eyebrow}
            </Typography>
          )}
        </Box>
      </Box>
      {headlineText && (
        <Typography
          component="h1"
          sx={{ mt: 3, fontSize: 28, lineHeight: 1.15, fontWeight: 800, letterSpacing: '-0.02em', fontFamily: DISPLAY_FONT }}
        >
          {headlineText}
        </Typography>
      )}
      {extra}
    </Box>
  );

  if (done) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: tok('surface') }}>
        {header()}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 80, height: 80, borderRadius: '26px', mb: 3,
              background: tok('action'), color: tok('onAction'),
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h5" gutterBottom sx={{ color: tok('onSurface'), fontFamily: DISPLAY_FONT, fontWeight: 700 }}>
            Photos sent to {name}
          </Typography>
          <Typography variant="body2" sx={{ color: tok('onSurfaceMuted'), mb: 2 }}>
            They'll see them with your request.
          </Typography>
          <Link component={RouterLink} to={`/form/${data.formId}`} sx={{ mt: 1, color: tok('action') }}>
            Open the conversation
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', background: tok('background') }}>
      {header(
        headline,
        questions.length > 1 ? (
          <Box component="ul" sx={{ m: 0, mt: 1.5, pl: 2.5, color: tok('onBackgroundMuted'), fontSize: 15, lineHeight: 1.5 }}>
            {questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </Box>
        ) : undefined
      )}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          mt: -2,
          px: 2.5,
          pt: 3,
          pb: 'max(24px, env(safe-area-inset-bottom))',
          background: tok('surface'),
          borderRadius: '24px 24px 0 0',
          position: 'relative',
        }}
      >
        <Typography variant="body2" sx={{ color: tok('onSurfaceMuted'), mb: 2.5 }}>
          Snap or choose up to {MAX_PHOTOS} photos, then tap Send.
        </Typography>

        <Card sx={{ p: 2, mb: 3, background: tok('card'), border: '1px solid', borderColor: tok('cardBorder'), boxShadow: 'none', borderRadius: '16px' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(84px, 1fr))',
              gap: 1.25,
              mb: photos.length > 0 ? 2 : 0,
            }}
          >
            {photos.map((photo) => (
              <Box
                key={photo.id}
                sx={{
                  position: 'relative', width: '100%', aspectRatio: '1 / 1',
                  borderRadius: '12px', overflow: 'hidden',
                  border: '1px solid', borderColor: tok('cardBorder'),
                }}
              >
                <Box component="img" src={photo.previewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <IconButton
                  size="small"
                  onClick={() => removePhoto(photo.id)}
                  disabled={uploading}
                  aria-label="Remove photo"
                  sx={{
                    position: 'absolute', top: 2, right: 2, width: 22, height: 22,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)', color: '#fff',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}
          </Box>

          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || photos.length >= MAX_PHOTOS}
            sx={{
              borderRadius: 3, py: 1.25, borderWidth: 1.5,
              color: tok('onSurface'), borderColor: tok('action'),
              '&:hover': { borderWidth: 1.5, borderColor: tok('action'), background: tok('surface') },
            }}
          >
            {photos.length === 0 ? 'Choose photos' : 'Add more'}
          </Button>
          <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFilesSelected} />
        </Card>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={handleDone}
          disabled={photos.length === 0 || uploading}
          sx={{
            borderRadius: 3, py: 1.5, fontWeight: 700, boxShadow: 'none',
            background: tok('action'), color: tok('onAction'),
            '&:hover': { background: tok('action'), boxShadow: 'none', filter: 'brightness(0.95)' },
            '&.Mui-disabled': { background: 'rgba(0, 0, 0, 0.12)', color: 'rgba(0, 0, 0, 0.4)' },
          }}
        >
          {uploading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CircularProgress size={18} sx={{ color: 'inherit' }} />
              <span>{progressText || 'Sending…'}</span>
            </Box>
          ) : (
            photos.length > 0 ? `Send ${photos.length} photo${photos.length === 1 ? '' : 's'}` : 'Send'
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ContinuationScreen;
